import { GoogleGenerativeAI } from "@google/generative-ai";

const API_KEY = import.meta.env.VITE_GEMINI_API_KEY;

// ---------------------------------------------------------
// 1. NURSING EXAM CURRICULUM (Kenntnisprüfung)
// ---------------------------------------------------------
export const NURSING_CURRICULUM = {
  INTERNAL_MEDICINE: {
    id: "INTERNAL_MEDICINE",
    title: "Innere Medizin",
    topics: [
      "Cardiovascular diseases",
      "Diabetes mellitus",
      "Respiratory system",
      "Gastrointestinal tract"
    ]
  },
  SURGERY: {
    id: "SURGERY",
    title: "Chirurgie",
    topics: [
      "Wound management",
      "Perioperative care",
      "Fractures",
      "Patient positioning"
    ]
  },
  GERIATRICS: {
    id: "GERIATRICS",
    title: "Geriatrie & Palliative Care",
    topics: [
      "Dementia",
      "Decubitus prophylaxis",
      "Pain management",
      "End-of-life care"
    ]
  },
  PEDIATRICS: {
    id: "PEDIATRICS",
    title: "Pädiatrie",
    topics: [
      "Child development",
      "Common childhood illnesses",
      "Communication with parents"
    ]
  },
  LAW_ETHICS: {
    id: "LAW_ETHICS",
    title: "Berufsrecht & Ethik",
    topics: [
      "Nursing profession law (PflBG)",
      "Patient rights",
      "Liability",
      "Documentation"
    ]
  },
  EMERGENCY: {
    id: "EMERGENCY",
    title: "Notfallmanagement",
    topics: [
      "Reanimation",
      "Shock",
      "First aid measures",
      "Communication in crisis"
    ]
  }
};

// ---------------------------------------------------------
// 2. SYSTEM INSTRUCTIONS
// ---------------------------------------------------------

const INSTRUCTION_EXAMINER = `
You are a professional Nurse Examiner conducting an oral "Kenntnisprüfung" (knowledge test) for an international nursing student in Germany.
**YOUR GOAL:**
Assess the student's technical knowledge and their ability to communicate professionally in German.
**CONDUCT:**
1. **Persona:** Professional, objective, yet encouraging. You are an experienced German nurse.
2. **Context:** This is an oral exam. You should present a short case study or ask technical questions.
3. **Language:** ALWAYS respond in German ONLY. No English translations.
4. **Logic:** 
   - Start by introducing yourself and the topic of the exam.
   - Ask one question at a time.
   - Wait for the student's response.
   - Correct technical errors or language mistakes gently but strictly (as an examiner would).
   - If the student is struggling, provide a small hint in German.
5. **JSON Format:** You MUST return a JSON object:
   {
     "german": "Examiner's response in German..."
   }
**FORBIDDEN:** 
- Do NOT use '*' or special formatting characters.
- Keep responses concise (max 3-4 sentences).
`;

const INSTRUCTION_ANALYZER = `
You are a Curriculum Analyzer for a Nursing Exam.
**CURRICULUM:**
${JSON.stringify(NURSING_CURRICULUM)}
**GOAL:**
Analyze the student's performance and update their progress history.
**STRUCTURE:**
History is an object where keys are Module IDs and values are arrays of performance logs.
**JSON OUTPUT:**
{
  "updated_history": {
    "MODULE_ID": [{ "positive": number, "negative": number, "topic": "string" }],
    ...
  }
}
`;

// ---------------------------------------------------------
// 3. STORAGE & INITIALIZATION
// ---------------------------------------------------------

let genAI: any = null;

function initializeGemini() {
  if (!genAI && API_KEY) {
    genAI = new GoogleGenerativeAI(API_KEY);
  }
}

export function getInitialHistory() {
  const history: any = {};
  Object.keys(NURSING_CURRICULUM).forEach((key) => {
    history[key] = [];
  });
  return history;
}

export function getStoredHistory(userId: string) {
  const stored = localStorage.getItem(`exam-sim-history-${userId}`);
  if (!stored) return getInitialHistory();
  try {
    return JSON.parse(stored);
  } catch (e) {
    return getInitialHistory();
  }
}

export function saveStoredHistory(userId: string, history: any) {
  localStorage.setItem(`exam-sim-history-${userId}`, JSON.stringify(history));
}

// ---------------------------------------------------------
// 4. MAIN EXPORT FUNCTIONS
// ---------------------------------------------------------

const MODELS = ["gemini-2.5-flash", "gemini-2.0-flash", "gemini-flash-latest"];

export async function sendMessageToGemini(
  userId: string,
  history: any[],
  newMessage: string
) {
  initializeGemini();
  if (!genAI) throw new Error("Gemini API Key missing (VITE_GEMINI_API_KEY)");

  const currentStatus = getStoredHistory(userId);

  const chatHistory = history.map((msg) => ({
    role: msg.sender === "user" ? "user" : "model",
    parts: [
      {
        text: typeof msg.text === "object" ? JSON.stringify(msg.text) : msg.text
      }
    ]
  }));

  const prompt = `
    [CURRENT PROGRESS]: ${JSON.stringify(currentStatus)}
    [USER INPUT]: ${newMessage}
    
    Respond as the Nurse Examiner in JSON format.
  `;

  for (const modelName of MODELS) {
    try {
      const model = genAI.getGenerativeModel({
        model: modelName,
        systemInstruction: INSTRUCTION_EXAMINER,
        generationConfig: { responseMimeType: "application/json" }
      });

      const chat = model.startChat({ history: chatHistory });
      const result = await chat.sendMessage(prompt);
      const text = result.response.text();
      const parsed = JSON.parse(text);

      // Background task for Brain Analysis (Brain Thread)
      // Since we don't have parallel background workers in browser easily without Workers,
      // we'll just run it and let it finish.
      analyzeProgress(userId, currentStatus, newMessage);

      return {
        message: parsed,
        model: modelName
      };
    } catch (e) {
      console.warn(`Model ${modelName} failed:`, e);
      continue;
    }
  }

  throw new Error("All Gemini models failed.");
}

async function analyzeProgress(
  userId: string,
  currentHistory: any,
  userInput: string
) {
  try {
    const model = genAI.getGenerativeModel({
      model: "gemini-2.5-flash",
      systemInstruction: INSTRUCTION_ANALYZER,
      generationConfig: { responseMimeType: "application/json" }
    });

    const prompt = `
      [HISTORY]: ${JSON.stringify(currentHistory)}
      [USER INPUT]: ${userInput}
      Analyze and update the history.
    `;

    const result = await model.generateContent(prompt);
    const text = result.response.text();
    const parsed = JSON.parse(text);

    if (parsed.updated_history) {
      saveStoredHistory(userId, parsed.updated_history);
    }
  } catch (e) {
    console.error("Progress analysis failed:", e);
  }
}
